import useActiveScreen from 'app/context/Analytics/useActiveScreen';
import useLinks from 'app/modules/tailor/hooks/useLinks';
import useTailorTracking from 'app/modules/tailor/hooks/useTailorTracking';
import { rootContainer } from 'app/shadowRoot';
import { MouseEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import useIsIntegratedView from './useIsIntegratedView';

const TOOLTIP_HREF = '#';

export default function useHTMLLinkClickHandler() {
  const navigate = useNavigate();
  const isIntegratedView = useIsIntegratedView();
  const { openLink } = useLinks();
  const { activeScreen } = useActiveScreen();
  const { captureLandingPageLinkClicked } = useTailorTracking();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const link = (event.target as HTMLElement).closest('a');

      if (!link) return;

      event.preventDefault();
      const url = link.href;

      // Handle tooltip links
      const { content, anchor } = link.dataset;
      if (content && anchor && link.getAttribute('href') === TOOLTIP_HREF) {
        navigate('/live-chat/tooltip', {
          state: {
            content,
            anchor,
          },
        });
        return;
      }

      // Handle hash links for scrolling to elements
      const hash = new URL(url || '#', window.location.origin).hash;
      if (hash && hash !== TOOLTIP_HREF) {
        // Check if hash is a valid CSS selector (starts with # and contains only valid characters)
        const isValidSelector = /^#[a-zA-Z][\w-]*$/.test(hash);

        if (isValidSelector) {
          const targetElement = rootContainer.querySelector(hash);

          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: isIntegratedView ? 'nearest' : 'start',
            });

            return;
          }
        }
      }

      // Handle internal Ostro links to Terms of Use and Privacy Policy
      const routesMap = [
        {
          regex: /ostrohealth.com\/terms-of-use/gim,
          path: '/tailor/info/terms',
        },
        {
          regex: /ostrohealth.com\/privacy-policy/gim,
          path: '/tailor/info/privacy',
        },
      ];

      for (const { regex, path } of routesMap) {
        if (regex.test(url)) {
          if (isIntegratedView) {
            window.open(url, '_blank');
            return;
          }

          navigate(path);
          return;
        }
      }

      // Handle all other links
      if (activeScreen === 'LandingPage') {
        captureLandingPageLinkClicked(url);
      }
      openLink(url);
    },
    [navigate, openLink, activeScreen, captureLandingPageLinkClicked, isIntegratedView]
  );

  return handleClick;
}
